import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaEye, FaEdit, FaTrash, FaPlus, FaSearch, FaFilter, FaCalendarAlt } from 'react-icons/fa';
import { useTitle } from '../../../context/TitleContext';
import { fetchPromoCodes, deletePromoCode, clearError, clearSuccess } from '../../../redux/Slices/PromoCodesSlice';
import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastHelper';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import { ToastContainer } from 'react-toastify';

const StorePromoCodes = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();

  const { promoCodes, pagination, loading, error, success } = useSelector(state => state.promoCodes);

  const [filters, setFilters] = useState({
    page: 1,
    from: '',
    to: '',
    code: '',
    status: ''
  });

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    action: null,
    data: null
  });

  useEffect(() => {
    setTitle('البرومو كود');
    dispatch(fetchPromoCodes(filters));
  }, [dispatch, filters.page]);

  useEffect(() => {
    if (error) {
      showErrorToast(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      showSuccessToast(success);
      dispatch(clearSuccess());
      dispatch(fetchPromoCodes(filters));
    }
  }, [success, dispatch, filters]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1
    }));
  };

  const handleSearch = () => {
    dispatch(fetchPromoCodes(filters));
  };

  const handleClearFilters = () => {
    setFilters({
      page: 1,
      from: '',
      to: '',
      code: '',
      status: ''
    });
    dispatch(fetchPromoCodes({
      page: 1,
      from: '',
      to: '',
      code: '',
      status: ''
    }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleView = (promoCodeId) => {
    navigate(`/store-promo-codes/${promoCodeId}`);
  };

  const handleEdit = (promoCodeId) => {
    navigate(`/store-promo-codes/${promoCodeId}/edit`);
  };

  const handleDelete = (promoCode) => {
    setConfirmModal({
      isOpen: true,
      action: 'delete',
      data: promoCode.id
    });
  };

  const handleConfirmAction = () => {
    if (confirmModal.action === 'delete') {
      dispatch(deletePromoCode(confirmModal.data));
      setConfirmModal({ isOpen: false, action: null, data: null });
    }
  };

  const getStatusBadge = (status) => {
    const statusInfo = {
      1: { class: 'bg-success', text: 'نشط' },
      0: { class: 'bg-danger', text: 'غير نشط' },
      true: { class: 'bg-success', text: 'نشط' },
      false: { class: 'bg-danger', text: 'غير نشط' }
    };
    
    const currentStatus = statusInfo[status] || { class: 'bg-secondary', text: 'غير محدد' };
    
    return (
      <span className={`badge ${currentStatus.class} text-white`} style={{ fontSize: '0.75rem', padding: '0.35em 0.65em' }}>
        {currentStatus.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'غير محدد';
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderPagination = () => {
    if (pagination.last_page <= 1) return null;

    const pages = [];
    const startPage = Math.max(1, pagination.current_page - 2);
    const endPage = Math.min(pagination.last_page, pagination.current_page + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={`page-item ${i === pagination.current_page ? 'active' : ''}`}>
          <button
            className="page-link"
            onClick={() => handlePageChange(i)}
            disabled={loading}
          >
            {i}
          </button>
        </li>
      );
    }

    return (
      <nav aria-label="Page navigation">
        <ul className="pagination justify-content-center">
          <li className={`page-item ${pagination.current_page === 1 ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(pagination.current_page - 1)}
              disabled={pagination.current_page === 1 || loading}
            >
              السابق
            </button>
          </li>
          {pages}
          <li className={`page-item ${pagination.current_page === pagination.last_page ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(pagination.current_page + 1)}
              disabled={pagination.current_page === pagination.last_page || loading}
            >
              التالي
            </button>
          </li>
        </ul>
      </nav>
    );
  };

  return (
    <div className="container-fluid">
      <ToastContainer />
      
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center my-4">
        <h4 className="mb-0">البرومو كود</h4>
        <button
          className="btn d-flex align-items-center gap-2" style={{ backgroundColor: 'rgb(24, 65, 148)', color: 'white' }}
          onClick={() => navigate('/store-promo-codes/add')}
        >
          <FaPlus />
          إضافة برومو كود
        </button>
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card mb-4"
      >
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">كود البرومو</label>
              <input
                type="text"
                className="form-control"
                name="code"
                value={filters.code}
                onChange={handleInputChange}
                placeholder="ابحث بالكود"
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">من تاريخ</label>
              <input
                type="date"
                className="form-control"
                name="from"
                value={filters.from}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">إلى تاريخ</label>
              <input
                type="date"
                className="form-control"
                name="to"
                value={filters.to}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">الحالة</label>
              <select
                className="form-select"
                name="status"
                value={filters.status}
                onChange={handleInputChange}
              >
                <option value="">جميع الحالات</option>
                <option value="1">نشط</option>
                <option value="0">غير نشط</option>
              </select>
            </div>
          </div>
          <div className="row mt-3">
            <div className="col-12 d-flex gap-2 justify-content-center">
              <button
                className="btn d-flex align-items-center justify-content-center gap-2" style={{ backgroundColor: 'rgb(24, 65, 148)', color: 'white', minWidth: '120px' }}
                onClick={handleSearch}
                disabled={loading}
              >
                <FaSearch />
                بحث
              </button>
              <button
                className="btn btn-secondary d-flex align-items-center gap-2"
                onClick={handleClearFilters}
                disabled={loading}
                style={{ minWidth: '120px' }}
              >
                <FaFilter />
                مسح الفلاتر
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card"
      >
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">جاري التحميل...</span>
              </div>
            </div>
          ) : !promoCodes || !Array.isArray(promoCodes) || promoCodes.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">لا توجد برومو كود</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-modern">
                  <thead>
                    <tr>
                      <th>الكود</th>
                      <th>نسبة الخصم</th>
                      <th>الحد الأدنى للطلب</th>
                      <th>حد الاستخدام</th>
                      <th>المستخدم</th>
                      <th>تاريخ البداية</th>
                      <th>تاريخ الانتهاء</th>
                      <th>الحالة</th>
                      <th>الإجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promoCodes.map((promoCode) => (
                      <tr key={promoCode.id}>
                        <td>
                          <span className="fw-bold text-primary">
                            {promoCode.code}
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-info text-white">
                            {Math.round(promoCode.discount_percent)}%
                          </span>
                        </td>
                        <td>{promoCode.min_order || 0} جنيه</td>
                        <td>{promoCode.usage_limit || 'غير محدود'}</td>
                        <td>{promoCode.used_count || 0}</td>
                        <td>{formatDate(promoCode.starts_at)}</td>
                        <td>{formatDate(promoCode.expires_at)}</td>
                        <td>{getStatusBadge(promoCode.status)}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <button
                              className="btn btn-view btn-sm"
                              onClick={() => handleView(promoCode.id)}
                              title="عرض"
                            >
                              <FaEye />
                            </button>
                            <button
                              className="btn btn-ban btn-sm"
                              onClick={() => handleEdit(promoCode.id)}
                              title="تعديل"
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="btn btn-unban btn-sm"
                              onClick={() => handleDelete(promoCode)}
                              title="حذف"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {renderPagination()}
            </>
          )}
        </div>
      </motion.div>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, action: null, data: null })}
        onConfirm={handleConfirmAction}
        title="تأكيد الحذف"
        message="هل أنت متأكد من حذف هذا البرومو كود؟"
        confirmText="حذف"
        cancelText="إلغاء"
        type="danger"
      />

      <style jsx>{`
        .table-modern {
          border-collapse: separate;
          border-spacing: 0;
          border: 1px solid #dee2e6;
          border-radius: 0.375rem;
          overflow: hidden;
        }
        
        .table-modern thead th {
          background-color: #f8f9fa;
          border-bottom: 2px solid #dee2e6;
          font-weight: 600;
          color: #495057;
          padding: 1rem 0.75rem;
        }
        
        .table-modern tbody td {
          padding: 1rem 0.75rem;
          border-bottom: 1px solid #dee2e6;
          vertical-align: middle;
        }
        
        .table-modern tbody tr:hover {
          background-color: #f8f9fa;
        }
        
        .btn-view {
          background-color: #17a2b8;
          border-color: #17a2b8;
          color: white;
        }
        
        .btn-view:hover {
          background-color: #138496;
          border-color: #117a8b;
          color: white;
        }
        
        .btn-ban {
          background-color: #ffc107;
          border-color: #ffc107;
          color: #212529;
        }
        
        .btn-ban:hover {
          background-color: #e0a800;
          border-color: #d39e00;
          color: #212529;
        }
        
        .btn-unban {
          background-color: #dc3545;
          border-color: #dc3545;
          color: white;
        }
        
        .btn-unban:hover {
          background-color: #c82333;
          border-color: #bd2130;
          color: white;
        }
        
        .pagination .page-link {
          color: #007bff;
          border-color: #dee2e6;
        }
        
        .pagination .page-item.active .page-link {
          background-color: #007bff;
          border-color: #007bff;
        }
        
        .pagination .page-link:hover {
          color: #0056b3;
          background-color: #e9ecef;
          border-color: #dee2e6;
        }
      `}</style>
    </div>
  );
};

export default StorePromoCodes;
